import {
  Alert,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Stepper,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import { formatMedplumClientError } from '../appointments/utils/medplumClientErrors';
import { useMedplum, useQuestionnaireForm } from '@medplum/react-hooks';
import type { Bundle, Questionnaire, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { IconAlertCircle, IconArrowLeft, IconLanguage } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClientPortalSignatureSection } from '../../components/ClientPortalSignatureSection';
import {
  isClientPortalSignatureValid,
  validateRequiredTemplateFields,
  validateRequiredTemplateFieldsInScope,
} from '../../lib/questionnaireResponseFillValidation';
import { scrollToAndFlashQuestionnaireItem } from '../appointments/utils/scrollToQuestionnaireItem';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isChartDocumentClientSignatureRequired, resolveProviderLockedLinkIdsForClientView } from '../../lib/chart/chartDocumentWorkflow';
import { readQuestionnaireForResponseRef } from '../../lib/chart/chartQuestionnaireRef';
import { ensureCoordinationRoiPrefillOnResponse, resolveCoordinationRoiClientReadOnlyLinkIds } from '../../lib/chart/coordinationOfCareRoi';
import { shapeResponseItemsForQuestionnaire } from '../appointments/utils/questionnaireResponseShape';
import { CLIENT_DOCUMENT_COMPLETION_CODE, CLIENT_SIGNATURE_REQUEST_CODE } from '../../lib/taskFhirConstants';
import { isAnyAssessmentTask, isFormRequestTask } from '../../lib/clientFacingTasks';
import { ClientPortalLayout } from './components/ClientPortalLayout';
import { PreviewItemArray } from '../settings/template-library/components/TemplatePreviewDrawer';
import {
  DocumentFormatFooter,
  DocumentFormatLogo,
} from '../settings/template-library/components/DocumentFormatBranding';
import { useDocumentFormatPresentation } from '../settings/template-library/hooks';
import { getMockTemplates } from '../settings/template-library/mockTemplates';
import {
  resolveQuestionnaireVariables,
  useVariableContext,
} from '../../lib/template-variables';
import {
  assignerPractitionerIdFromTask,
} from '../../lib/template-variables/practitionerContext';
import { practitionerRequiresCosignature } from '@unify/fhir-model/resources/practitioner';
import { getLanguageDisplay } from '@/lib/useLanguageOptions';
import { submitClientPortalFormTask } from '../../lib/portalFormSubmissionService';
import { useDocumentLanguageVariants } from './hooks/useDocumentLanguageVariants';
import { hasSignatureRequired } from '../../lib/template-setup/questionnaire';
import {
  CLIENT_SIGNATURE_EXT_URL,
  getSignatureFromQR,
  setSignatureOnQR,
  type SignatureInfo,
} from '../appointments/fhirNoteMapping';

/** Task kind inferred from Task.code, used to select labels (not bot routing). */
type TaskKind = 'assessment' | 'form' | 'signature' | 'unknown';

const MOCK_TASK_PREFIX = 'mock-';

/**
 * Map a raw bot error string to a user-friendly message.
 * Surfaces specific actionable errors instead of the generic "Please try again."
 *
 * Order matters: specific Task / Questionnaire / Bot failures must run before the broad
 * `/not found|404/` bucket, which previously collapsed "bot missing" and
 * "Questionnaire not found" into "The assignment could not be found."
 */
export function classifyBotError(err: unknown): string {
  const msg = formatMedplumClientError(err) || normalizeErrorString(err);
  if (/no executable code/i.test(msg)) {
    return 'The submission service is not yet deployed. Please contact your care team.';
  }
  if (/not open|already.*complet|form may already be completed|status.*completed/i.test(msg)) {
    return 'This assignment is no longer open — it may have already been submitted.';
  }
  // Bot message: "Task/… is assigned to Patient/X, not the authenticated patient Patient/Y"
  // Must run before broad /forbidden/ checks — do NOT use /not.*patient/ (it matches this string too).
  if (/not the authenticated patient/i.test(msg)) {
    return (
      'This task is tied to a different patient than the account you are signed in with. ' +
      'If the portal lets you switch clients, switch to the correct profile and try again. ' +
      'Otherwise sign in as that patient, or ask your care team to re-send the assignment.'
    );
  }
  if (/not linked to this signed-in portal account/i.test(msg)) {
    return (
      'This assignment is for a patient that is not linked to your portal account. ' +
      'Switch to the correct client in the portal if available, or contact your care team.'
    );
  }
  if (/only authenticated patients can/i.test(msg)) {
    return 'Client submission requires a patient sign-in. You are not logged in as a patient.';
  }
  if (/only authenticated patient or related-person portal accounts/i.test(msg)) {
    return (
      'We could not verify your portal session for submission. Sign out and sign back in, ' +
      'or contact your care team if this keeps happening.'
    );
  }
  if (/forbidden|permission|unauthorized/i.test(msg)) {
    return 'You do not have permission to submit this form. Please contact your care team.';
  }
  if (/is not a measures template|is a scored measures template/i.test(msg)) {
    return 'This form type cannot be submitted through the current flow. Please contact your care team.';
  }
  if (/recurring.*schedule.*record|individual.*assignment.*due/i.test(msg)) {
    return (
      'This is a recurring schedule record, not a fillable assignment. ' +
      'Your portal will show the individual assessment when it is due. ' +
      'If you believe this is an error, contact your care team.'
    );
  }
  if (/not a client form assignment|use the assessment submission/i.test(msg)) {
    return 'This task cannot be submitted as a form. Please contact your care team.';
  }
  if (/questionnaire\/\S+ not found|questionnaire.*not found|template.*not found/i.test(msg)) {
    return 'The form template could not be found. It may have been removed or is inactive.';
  }
  if (/no questionnaire focus/i.test(msg)) {
    return 'This task has no form template attached. Please contact your care team.';
  }
  if (/task\/\S+ not found|task.*not found/i.test(msg)) {
    return 'The assignment could not be found. It may have been cancelled.';
  }
  // Bot registry / $execute 404 — form load succeeds without the bot, so this is the usual
  // "just assigned, submit fails" case when assessment-submission is not registered.
  if (
    /bot.*(not found|404)|executebot|\$execute|assessment-submission|form-submission/i.test(msg)
    || (/not found|404/i.test(msg) && /bot/i.test(msg))
  ) {
    return 'The submission service is not available in this environment. Please contact your care team.';
  }
  if (/client signature is required/i.test(msg)) {
    return 'Your signature is required before you can submit this document.';
  }
  if (/was not closed after submission|not closed after submission/i.test(msg)) {
    return 'Your answers were saved but the assignment could not be marked complete. Please contact your care team.';
  }
  if (/unknown linkId|completed by your provider and cannot be changed/i.test(msg)) {
    return msg;
  }
  if (/failed to save completed document|chart document completion/i.test(msg)) {
    return msg;
  }
  if (/not awaiting client completion/i.test(msg)) {
    return 'This document is no longer open for client completion.';
  }
  // Last-resort bucket for remaining not-found / 404 failures (often a missing bot id).
  if (/not found|404/i.test(msg)) {
    return (
      'The assignment or submission service could not be found. ' +
      'If this was just assigned, ask your care team to confirm the submission service is deployed.'
    );
  }
  return 'Submission failed. Please try again or contact your care team.';
}

export function AssessmentFillPage(): JSX.Element {
  const { taskId } = useParams<{ taskId: string }>();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    returnTo?: string;
    assessmentMeta?: { assessmentType: string; maxScore: number };
  } | null;

  const hideBackButton = !!locationState?.returnTo;

  const [task, setTask] = useState<Task | null>(null);
  const [taskKind, setTaskKind] = useState<TaskKind>('unknown');
  const [baseQuestionnaire, setBaseQuestionnaire] = useState<Questionnaire | null>(null);
  const [activeQuestionnaire, setActiveQuestionnaire] = useState<Questionnaire | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [swappingLanguage, setSwappingLanguage] = useState(false);
  const [isMockAssessment, setIsMockAssessment] = useState(false);
  const [assignerRequiresCosignature, setAssignerRequiresCosignature] = useState(false);
  const [existingResponse, setExistingResponse] = useState<QuestionnaireResponse | null>(null);
  const [readOnlyLinkIds, setReadOnlyLinkIds] = useState<ReadonlySet<string>>(new Set());
  const [clientSignatureRequired, setClientSignatureRequired] = useState(false);

  const assignerPractitionerId = useMemo(
    () => assignerPractitionerIdFromTask(task ?? undefined),
    [task],
  );

  const docFormat = useDocumentFormatPresentation({
    requiresCosignature: assignerRequiresCosignature,
    assignerPractitionerId,
  });

  useEffect(() => {
    if (!taskId) return;
    if (taskId.startsWith(MOCK_TASK_PREFIX)) {
      const templateId = taskId.slice(MOCK_TASK_PREFIX.length);
      const template = getMockTemplates().find((t) => t.id === templateId);
      if (template?.resource) {
        setBaseQuestionnaire(template.resource);
        setActiveQuestionnaire(template.resource);
        setTask(null);
        setIsMockAssessment(true);
        setAssignerRequiresCosignature(false);
      } else {
        setLoadError('Assessment template not found.');
      }
      return;
    }
    (async () => {
      try {
        const t = await medplum.readResource('Task', taskId);
        setTask(t);

        // Classify task so the UI picks the right labels and submission path
        if (isAnyAssessmentTask(t)) {
          setTaskKind('assessment');
        } else if (isFormRequestTask(t)) {
          setTaskKind('form');
        } else {
          setTaskKind('unknown');
        }

        const ref = t.focus?.reference;
        if (!ref) throw new Error('Task has no focus reference');
        const [rt, id] = ref.split('/');
        if (!id) throw new Error('Invalid focus reference');

        if (rt === 'QuestionnaireResponse') {
          const loadedQr = await medplum.readResource('QuestionnaireResponse', id);
          const qRef = loadedQr.questionnaire;
          if (!qRef) throw new Error('Document response is missing a template reference.');
          const q = await readQuestionnaireForResponseRef(medplum, qRef);
          const qr = await ensureCoordinationRoiPrefillOnResponse(medplum, q, loadedQr);
          setExistingResponse(qr);
          setReadOnlyLinkIds(resolveCoordinationRoiClientReadOnlyLinkIds(
            q,
            resolveProviderLockedLinkIdsForClientView(q, qr),
          ));
          setBaseQuestionnaire(q);
          setActiveQuestionnaire(q);
          const isChartDoc = t.code?.coding?.some(
            (c) => c.code === CLIENT_DOCUMENT_COMPLETION_CODE,
          );
          if (isChartDoc) setTaskKind('form');
          setClientSignatureRequired(
            isChartDocumentClientSignatureRequired(qr)
            || hasSignatureRequired(q)
            || (t.code?.coding?.some((c) => c.code === CLIENT_SIGNATURE_REQUEST_CODE) ?? false),
          );
          setIsMockAssessment(false);

          const assignerId = assignerPractitionerIdFromTask(t);
          if (assignerId) {
            try {
              const assigner = await medplum.readResource('Practitioner', assignerId);
              setAssignerRequiresCosignature(practitionerRequiresCosignature(assigner));
            } catch {
              setAssignerRequiresCosignature(false);
            }
          } else {
            setAssignerRequiresCosignature(false);
          }
          return;
        }

        if (rt !== 'Questionnaire') throw new Error('Invalid focus reference');
        const q = await medplum.readResource('Questionnaire', id);
        setBaseQuestionnaire(q);
        setActiveQuestionnaire(q);
        setExistingResponse(null);
        setReadOnlyLinkIds(new Set());
        setClientSignatureRequired(
          hasSignatureRequired(q)
          || (t.code?.coding?.some((c) => c.code === CLIENT_SIGNATURE_REQUEST_CODE) ?? false),
        );
        setIsMockAssessment(false);

        const assignerId = assignerPractitionerIdFromTask(t);
        if (assignerId) {
          try {
            const assigner = await medplum.readResource('Practitioner', assignerId);
            setAssignerRequiresCosignature(practitionerRequiresCosignature(assigner));
          } catch {
            setAssignerRequiresCosignature(false);
          }
        } else {
          setAssignerRequiresCosignature(false);
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load');
      }
    })();
  }, [medplum, taskId]);

  /**
   * Swap to a different translated Questionnaire by canonical URL. Falls
   * back silently to the base template on lookup failure so the client is
   * never stranded with a broken form.
   */
  const handleLanguageChange = useCallback(
    async (templateUrl: string): Promise<void> => {
      if (!baseQuestionnaire) return;
      if (templateUrl === activeQuestionnaire?.url) return;
      if (templateUrl === baseQuestionnaire.url) {
        setActiveQuestionnaire(baseQuestionnaire);
        return;
      }
      setSwappingLanguage(true);
      try {
        const bundle: Bundle = await medplum.search('Questionnaire', {
          url: templateUrl,
          status: 'active',
          _sort: '-_lastUpdated',
          _count: '1',
        });
        const next = (bundle.entry ?? [])
          .map((e) => e.resource as Questionnaire | undefined)
          .find((q): q is Questionnaire => Boolean(q));
        if (next) {
          setActiveQuestionnaire(next);
        } else {
          // Variant unavailable — keep the current rendering, surface
          // a non-blocking notice so the client knows nothing changed.
          notifications.show({
            color: 'yellow',
            title: 'Translation unavailable',
            message: 'That language version isn\u2019t available right now. Continuing in the current language.',
          });
        }
      } catch (err) {
        console.error('Failed to swap language variant:', err);
        notifications.show({
          color: 'yellow',
          title: 'Translation unavailable',
          message: 'Could not load that language version. Continuing in the current language.',
        });
      } finally {
        setSwappingLanguage(false);
      }
    },
    [activeQuestionnaire?.url, baseQuestionnaire, medplum],
  );

  const initialClientSignature = useMemo(
    () => (existingResponse
      ? getSignatureFromQR(existingResponse, CLIENT_SIGNATURE_EXT_URL) ?? null
      : null),
    [existingResponse],
  );

  const portalPatientId = useMemo(() => {
    const ref = task?.for?.reference ?? existingResponse?.subject?.reference;
    return ref?.startsWith('Patient/') ? ref.replace('Patient/', '') : undefined;
  }, [existingResponse?.subject?.reference, task?.for?.reference]);

  if (loadError) {
    return (
      <ClientPortalLayout title="Form">
        <Container size="sm">
          <Alert icon={<IconAlertCircle size={20} />} color="red" variant="light" title="Error">
            {loadError}
          </Alert>
        </Container>
      </ClientPortalLayout>
    );
  }

  const showForm = activeQuestionnaire && (isMockAssessment || task);
  if (!showForm) {
    return (
      <ClientPortalLayout title="Loading...">
        <Group justify="center" py="xl"><Loader /></Group>
      </ClientPortalLayout>
    );
  }

  return (
    <ClientPortalLayout title="" showPageHeader={false}>
      <Stack gap="md">
        <DocumentFormatLogo logoUrl={docFormat.logoUrl} />
        <Paper p="lg" withBorder>
          <AssessmentForm
            footerText={docFormat.footerText}
            assignerPractitionerId={assignerPractitionerId}
            patientId={portalPatientId}
            questionnaire={activeQuestionnaire}
            questionnaireResponse={existingResponse ?? undefined}
            readOnlyLinkIds={readOnlyLinkIds}
            clientSignatureRequired={clientSignatureRequired}
            initialClientSignature={initialClientSignature}
            taskKind={taskKind}
            baseQuestionnaireUrl={baseQuestionnaire?.url}
            onLanguageChange={handleLanguageChange}
            swappingLanguage={swappingLanguage}
            submitting={submitting}
            hideBack={hideBackButton}
            onSubmit={async (qr) => {
            setSubmitting(true);
            try {
              if (isMockAssessment && taskId?.startsWith(MOCK_TASK_PREFIX)) {
                const templateId = taskId.slice(MOCK_TASK_PREFIX.length);
                const meta = locationState?.assessmentMeta;
                notifications.show({
                  color: 'green',
                  title: 'Submitted',
                  message: `${activeQuestionnaire.title ?? 'Form'} has been completed.`,
                });
                const now = new Date();
                const localYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const completedAssessment = meta ? {
                  id: `completed-${Date.now()}`,
                  assessmentType: meta.assessmentType,
                  date: localYmd,
                  time: now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
                  provider: 'Current Provider',
                  score: Math.floor(Math.random() * Math.round(meta.maxScore * 0.6)),
                  maxScore: meta.maxScore,
                  administeredBy: 'Current Provider',
                  status: 'Completed' as const,
                } : undefined;
                const returnTo = locationState?.returnTo;
                if (returnTo) {
                  navigate(returnTo, { state: { completedAssessment } });
                } else {
                  navigate('/client-portal', {
                    state: { completedMock: { templateId, completedAt: new Date().toISOString() } },
                  });
                }
                return;
              }
              if (!task) return;

              console.debug('[AssessmentFillPage] Submitting via bot:', {
                taskKind,
                taskId: task.id,
                taskCode: task.code?.coding,
                taskStatus: task.status,
                focus: task.focus?.reference,
                questionnaireId: activeQuestionnaire.id,
              });

              const submitResult = await submitClientPortalFormTask(medplum, {
                task,
                questionnaireResponse: qr,
              });

              const successTitle = taskKind === 'assessment' ? 'Assessment submitted' : 'Form submitted';
              const zoomWarning =
                submitResult.zoomResyncFailures && submitResult.zoomResyncFailures.length > 0
                  ? 'Your form was saved, but meeting recording settings are still syncing. Support has been notified.'
                  : undefined;
              const successMsg =
                zoomWarning ??
                `${activeQuestionnaire.title ?? (taskKind === 'assessment' ? 'Assessment' : 'Form')} has been completed.`;
              notifications.show({
                color: zoomWarning ? 'yellow' : 'green',
                title: successTitle,
                message: successMsg,
              });
              navigate('/client-portal');
            } catch (err) {
              // Log full OperationOutcome so developers can diagnose bot vs permissions vs data failures.
              console.error('[AssessmentFillPage] Submission failed. Full error:', err);
              if (err && typeof err === 'object' && 'outcome' in err) {
                console.error('[AssessmentFillPage] OperationOutcome:', JSON.stringify((err as { outcome: unknown }).outcome, null, 2));
              }
              const errorTitle = taskKind === 'assessment' ? 'Assessment submission failed' : 'Form submission failed';
              notifications.show({
                color: 'red',
                title: errorTitle,
                message: classifyBotError(err),
              });
            } finally {
              setSubmitting(false);
            }
          }}
          onBack={() => navigate(locationState?.returnTo ?? '/client-portal')}
          />
        </Paper>
      </Stack>
    </ClientPortalLayout>
  );
}

interface AssessmentFormProps {
  questionnaire: Questionnaire;
  questionnaireResponse?: QuestionnaireResponse;
  readOnlyLinkIds?: ReadonlySet<string>;
  clientSignatureRequired?: boolean;
  initialClientSignature?: SignatureInfo | null;
  /** Determines submit button label and success messaging. */
  taskKind: TaskKind;
  /** Canonical URL of the originally requested template — drives variant lookup. */
  baseQuestionnaireUrl: string | undefined;
  /** Called when the client selects a different language; argument is the new template URL. */
  onLanguageChange: (templateUrl: string) => void;
  /** True while a language swap is in flight (disables the dropdown). */
  swappingLanguage: boolean;
  submitting: boolean;
  hideBack?: boolean;
  footerText?: string;
  assignerPractitionerId?: string;
  patientId?: string;
  onSubmit: (qr: QuestionnaireResponse) => void;
  onBack: () => void;
}

function AssessmentForm({
  questionnaire,
  questionnaireResponse,
  readOnlyLinkIds,
  clientSignatureRequired = false,
  initialClientSignature = null,
  taskKind,
  baseQuestionnaireUrl,
  onLanguageChange,
  swappingLanguage,
  submitting,
  hideBack,
  footerText,
  assignerPractitionerId,
  patientId,
  onSubmit,
  onBack,
}: AssessmentFormProps): JSX.Element {
  const [clientSignature, setClientSignature] = useState<SignatureInfo | null>(
    initialClientSignature ?? null,
  );

  useEffect(() => {
    setClientSignature(initialClientSignature ?? null);
  }, [initialClientSignature]);

  const submitLabel = taskKind === 'assessment' ? 'Submit assessment' : 'Submit form';
  const wrapFormBody = (content: JSX.Element): JSX.Element => (
    <Stack gap="md">
      {content}
      <DocumentFormatFooter footerText={footerText} />
    </Stack>
  );
  // Substitute organization-scoped variables before rendering. Client-facing
  // surfaces use the `'placeholder'` strategy so any tokens we couldn't
  // resolve don't leak as raw `{{...}}` text to the patient.
  const { context } = useVariableContext({ assignerPractitionerId });
  const resolvedQuestionnaire = resolveQuestionnaireVariables(questionnaire, context, {
    onUnresolved: 'placeholder',
  });

  // Discover sibling templates for this form (by canonical URL of the
  // *originally requested* template — variants discovered relative to the
  // base do not change as the user toggles between languages).
  const { variants: languageVariants } = useDocumentLanguageVariants(baseQuestionnaireUrl);

  const languageSelectData = useMemo(
    () =>
      languageVariants.map((v) => ({
        value: v.templateUrl,
        label: v.isDefault ? 'Default' : getLanguageDisplay(v.languageCode ?? null, []),
      })),
    [languageVariants],
  );

  const showLanguageSelect = languageSelectData.length > 1;

  const languageSelector = showLanguageSelect ? (
    <Select
      size="xs"
      w={180}
      data={languageSelectData}
      value={questionnaire.url ?? null}
      onChange={(v) => v && onLanguageChange(v)}
      leftSection={<IconLanguage size={14} />}
      aria-label="Document language"
      disabled={swappingLanguage}
    />
  ) : null;

  const formDefaultValue = useMemo(() => {
    if (!questionnaireResponse) return undefined;
    return {
      ...questionnaireResponse,
      item: shapeResponseItemsForQuestionnaire(questionnaireResponse.item, questionnaire)
        ?? questionnaireResponse.item,
    };
  }, [questionnaire, questionnaireResponse]);

  const formState = useQuestionnaireForm({
    questionnaire: resolvedQuestionnaire,
    defaultValue: formDefaultValue,
    disablePagination: false,
  });

  const handleSubmit = useCallback(() => {
    if (formState.loading) return;

    const fieldValidation = validateRequiredTemplateFields(
      resolvedQuestionnaire,
      formState.questionnaireResponse,
      { skipLinkIds: readOnlyLinkIds },
    );
    if (!fieldValidation.success) {
      scrollToAndFlashQuestionnaireItem(fieldValidation.missingItemPath);
      notifications.show({
        color: 'yellow',
        title: 'Required fields missing',
        message: fieldValidation.message,
      });
      return;
    }

    if (clientSignatureRequired && !isClientPortalSignatureValid(clientSignature)) {
      notifications.show({
        color: 'yellow',
        title: 'Signature required',
        message: 'Please sign this document before submitting.',
      });
      return;
    }

    let qr = formState.questionnaireResponse;
    if (clientSignatureRequired && clientSignature) {
      qr = setSignatureOnQR(qr, CLIENT_SIGNATURE_EXT_URL, clientSignature);
    }
    onSubmit(qr);
  }, [
    clientSignature,
    clientSignatureRequired,
    formState,
    onSubmit,
    readOnlyLinkIds,
    resolvedQuestionnaire,
  ]);

  const handleNextPage = useCallback(() => {
    if ('loading' in formState && formState.loading) {
      return;
    }
    const pageValidation = validateRequiredTemplateFieldsInScope(
      resolvedQuestionnaire,
      formState.questionnaireResponse,
      formState.items,
      { skipLinkIds: readOnlyLinkIds },
    );
    if (!pageValidation.success) {
      scrollToAndFlashQuestionnaireItem(pageValidation.missingItemPath);
      notifications.show({
        color: 'yellow',
        title: 'Required fields missing',
        message: pageValidation.message,
      });
      return;
    }
    if (!('onNextPage' in formState)) {
      return;
    }
    formState.onNextPage();
  }, [formState, readOnlyLinkIds, resolvedQuestionnaire]);

  if (formState.loading) {
    return wrapFormBody(
      <Group justify="center" py="xl"><Loader size="sm" /></Group>,
    );
  }

  if (formState.items.length === 0) {
    return wrapFormBody(
      <Stack gap="md">
        <Text size="sm" c="dimmed">This assessment has no questions.</Text>
        {!hideBack && (
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back to documents
          </Button>
        )}
      </Stack>,
    );
  }

  if (formState.pagination) {
    const { pages, activePage, onPrevPage } = formState;
    const isLastPage = activePage >= pages.length - 1;

    return wrapFormBody(
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isLastPage) handleSubmit();
        }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Title order={4}>{resolvedQuestionnaire.title}</Title>
            <Group gap="sm">
              {languageSelector}
              {!hideBack && (
                <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={14} />} onClick={onBack}>
                  Back
                </Button>
              )}
            </Group>
          </Group>

          <Stepper active={activePage} allowNextStepsSelect={false} size="sm">
            {pages.map((page, pageIndex) => (
              <Stepper.Step key={page.linkId} label={page.title}>
                {pageIndex === activePage && (
                  <PreviewItemArray
                    formState={formState}
                    context={[]}
                    items={formState.items}
                    responseItems={formState.responseItems}
                    questionnaireItems={resolvedQuestionnaire.item}
                    readOnlyLinkIds={readOnlyLinkIds}
                    patientId={patientId}
                  />
                )}
              </Stepper.Step>
            ))}
          </Stepper>

          {isLastPage && clientSignatureRequired && (
            <ClientPortalSignatureSection
              signature={clientSignature}
              onSignatureChange={setClientSignature}
            />
          )}

          <Group justify="center" gap="sm" mt="md">
            {activePage > 0 && (
              <Button variant="default" onClick={onPrevPage}>Back</Button>
            )}
            {!isLastPage && (
              <Button type="button" onClick={handleNextPage}>
                Next
              </Button>
            )}
            {isLastPage && (
              <Button type="submit" loading={submitting}>
                {submitLabel}
              </Button>
            )}
          </Group>
        </Stack>
      </form>,
    );
  }

  return wrapFormBody(
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Title order={4}>{resolvedQuestionnaire.title}</Title>
          <Group gap="sm">
            {languageSelector}
            {!hideBack && (
              <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={14} />} onClick={onBack}>
                Back
              </Button>
            )}
          </Group>
        </Group>

        <PreviewItemArray
          formState={formState}
          context={[]}
          items={formState.items}
          responseItems={formState.responseItems}
          questionnaireItems={resolvedQuestionnaire.item}
          readOnlyLinkIds={readOnlyLinkIds}
          patientId={patientId}
        />

        {clientSignatureRequired && (
          <ClientPortalSignatureSection
            signature={clientSignature}
            onSignatureChange={setClientSignature}
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onBack}>Cancel</Button>
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>,
  );
}
